/* Halo V3 — dots for the phone-width media carousels.
 *
 * Three product-page rows stack vertically on a phone and give no hint
 * that there is more to see: the media under the Halo Lytt video, the
 * app cards, and the activity cards. CSS turns them into horizontal
 * scroll-snap rails with the next card peeking; this adds the position
 * dots, which CSS cannot do on its own.
 *
 * Deliberately small and defensive:
 *
 *   - Only runs under 700px, matching the CSS. Above that the rows are
 *     real multi-column grids and dots would be meaningless.
 *   - Only runs where there is more than one item to scroll to.
 *   - Tears itself down on resize, so rotating a phone or opening a
 *     desktop window does not leave orphan dots behind.
 *   - Reads scroll position rather than tracking clicks, so dragging,
 *     flicking and keyboard scrolling all stay in sync.
 *   - No dependency on the section markup beyond the container class,
 *     so a theme editor re-render cannot break it.
 */
(function () {
  'use strict';

  var SELECTOR = '.halo-pb-macros, .halo-pb-cards';
  var MOBILE = '(max-width: 699px)';
  var built = [];

  // Settings come off our own <script> tag, populated from theme settings.
  var cfg = document.currentScript
    || document.querySelector('script[src*="halo-v3-carousel"]');
  var AUTOPLAY = cfg ? cfg.dataset.autoplay === 'true' : true;
  var INTERVAL = Math.max(2, parseInt((cfg && cfg.dataset.interval) || '3', 10)) * 1000;

  // Never auto-advance for someone who has asked their device to reduce
  // motion. A carousel that moves on its own is exactly the kind of thing
  // that setting exists to stop.
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Positions are measured with getBoundingClientRect, NOT offsetLeft.
  // offsetLeft is relative to the nearest positioned ancestor, which for
  // these rails is somewhere further up the section, so subtracting
  // scrollLeft from it gave a meaningless number and the active dot never
  // moved off the first item. Rects are relative to the viewport for both
  // the rail and its children, so the difference is the true offset
  // inside the scroller regardless of how anything is positioned.
  function offsetWithin(rail, child) {
    return child.getBoundingClientRect().left - rail.getBoundingClientRect().left;
  }

  function activeIndex(rail) {
    var best = 0;
    var bestDist = Infinity;
    for (var i = 0; i < rail.children.length; i++) {
      var child = rail.children[i];
      if (child.classList && child.classList.contains('hv3-dots')) continue;
      var dist = Math.abs(offsetWithin(rail, child));
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    return best;
  }

  function build(rail) {
    var items = Array.prototype.filter.call(rail.children, function (c) {
      return !(c.classList && c.classList.contains('hv3-dots'));
    });
    if (items.length < 2) return;

    var dots = document.createElement('div');
    dots.className = 'hv3-dots';
    dots.setAttribute('role', 'tablist');
    dots.setAttribute('aria-label', 'Choose a slide');

    var buttons = items.map(function (item, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'hv3-dot';
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-label', 'Slide ' + (i + 1) + ' of ' + items.length);
      b.addEventListener('click', function () {
        // Same reason as above: scroll by the measured offset inside the
        // rail, not by offsetLeft.
        rail.scrollTo({
          left: rail.scrollLeft + offsetWithin(rail, item),
          behavior: 'smooth'
        });
      });
      dots.appendChild(b);
      return b;
    });

    function sync() {
      var idx = activeIndex(rail);
      for (var i = 0; i < buttons.length; i++) {
        var on = i === idx;
        buttons[i].classList.toggle('is-on', on);
        buttons[i].setAttribute('aria-selected', on ? 'true' : 'false');
      }
    }

    var raf = null;
    function onScroll() {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        sync();
      });
    }

    rail.addEventListener('scroll', onScroll, { passive: true });
    rail.parentNode.insertBefore(dots, rail.nextSibling);
    sync();

    /* ---------- autoplay ---------- */

    var timer = null;
    var stopped = false;
    var visible = false;

    // Marks scrolls we caused ourselves, so the handler below can tell an
    // automatic advance apart from a real swipe.
    var selfScrollUntil = 0;

    function advance() {
      var next = (activeIndex(rail) + 1) % items.length;
      selfScrollUntil = Date.now() + 900;
      rail.scrollTo({
        left: rail.scrollLeft + offsetWithin(rail, items[next]),
        behavior: 'smooth'
      });
    }

    function play() {
      if (stopped || timer || !visible) return;
      timer = setInterval(advance, INTERVAL);
    }

    function pause() {
      clearInterval(timer);
      timer = null;
    }

    // Once a visitor takes control, autoplay is done for good on that rail.
    // Pausing and resuming sounds friendlier but in practice it yanks the
    // slide away moments after someone stopped to read it.
    function stop() {
      stopped = true;
      pause();
    }

    var io = null;
    if (AUTOPLAY && !REDUCED && items.length > 1) {
      // Stopping on touchstart/wheel was wrong: a visitor scrolling the PAGE
      // with a finger or pointer over the carousel would kill autoplay
      // without ever having interacted with it. Instead, watch the rail's
      // own horizontal position and only stop when it moves for a reason we
      // did not cause. Vertical page scrolling never changes scrollLeft, so
      // it no longer counts.
      var lastLeft = rail.scrollLeft;
      rail.addEventListener('scroll', function () {
        if (rail.scrollLeft === lastLeft) return;
        lastLeft = rail.scrollLeft;
        if (Date.now() > selfScrollUntil) stop();
      }, { passive: true });

      buttons.forEach(function (b) { b.addEventListener('click', stop); });

      // Only run while the rail is actually on screen. Three carousels all
      // ticking through an unseen product page is wasted battery, and the
      // visitor would arrive mid-sequence for no reason.
      if ('IntersectionObserver' in window) {
        io = new IntersectionObserver(function (entries) {
          visible = entries[0].isIntersecting;
          if (visible) play(); else pause();
        }, { threshold: 0.4 });
        io.observe(rail);
      } else {
        visible = true;
        play();
      }

      // A backgrounded tab should not keep advancing.
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) pause(); else play();
      });
    }

    built.push({
      rail: rail,
      dots: dots,
      teardown: function () {
        pause();
        stopped = true;
        if (io) io.disconnect();
        rail.removeEventListener('scroll', onScroll);
        if (dots.parentNode) dots.parentNode.removeChild(dots);
      }
    });
  }

  function teardownAll() {
    built.forEach(function (b) { b.teardown(); });
    built = [];
  }

  function apply() {
    teardownAll();
    if (!window.matchMedia(MOBILE).matches) return;
    Array.prototype.forEach.call(document.querySelectorAll(SELECTOR), build);
  }

  var timer = null;
  window.addEventListener('resize', function () {
    clearTimeout(timer);
    timer = setTimeout(apply, 150);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }
})();
